import { type UseFormReturn, useForm, useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetPartnerAdminsUser, useGetPartnerAdminsUserBankAccount } from '~/hooks';
import { getUserLink } from '~/utils/getLinks';
import { convertToFormValues, generateRequest } from '../logics';
import {
  type EditUserFormBankAccountFieldValues,
  editUserFormBankAccountSchema,
} from '../logics/schema';
import type { EditUserFormBankAccountPresentation } from '../presentations';
import { usePutPartnerAdminsUserBankAccount } from './usePutPartnerAdminsUserBankAccount';

type PresentationProps = React.ComponentProps<typeof EditUserFormBankAccountPresentation>;

type IUseEditUserFormBankAccount = (userId: number) => {
  state: {
    methods: UseFormReturn<EditUserFormBankAccountFieldValues>;
    isButtonDisabled: boolean;
    /**
     * 口座情報 API レスポンス。null = 未連携（404）。
     * Container 層で convertToBankAccountDisplayFields を通して表示値に変換する。
     */
    bankAccountData: ReturnType<typeof useGetPartnerAdminsUserBankAccount>['data'];
    /**
     * GET /partner_admins/users/:userId のレスポンス。
     * Container 層で convertToPresentationProps を通して Presentation Props に変換する。
     */
    userData: ReturnType<typeof useGetPartnerAdminsUser>['data'];
  };
  actions: {
    handleOnSubmit: PresentationProps['onSubmit'];
  };
};

/**
 * 利用者編集フォーム（口座連携版）のカスタムフック
 *
 * - useGetPartnerAdminsUserBankAccount は suspense: true。
 *   useGetPartnerAdminsUser と同じ Suspense 境界で取得完了を待つ。
 *   404（未連携）は bankAccountData = null となり、Container で '-' に変換される。
 *
 * @param userId - 編集対象の利用者ID
 * @returns state.methods - react-hook-form のメソッド
 * @returns state.isButtonDisabled - 保存ボタン非活性フラグ
 * @returns state.bankAccountData - 口座情報 API レスポンス（null = 未連携）
 * @returns state.userData - 利用者情報 API レスポンス
 * @returns actions.handleOnSubmit - フォーム送信ハンドラ
 */
export const useEditUserFormBankAccount: IUseEditUserFormBankAccount = userId => {
  const navigate = useNavigate();
  const threeLetterCode = getThreeLetterCode();
  const { showToast } = useToast();

  const { data } = useGetPartnerAdminsUser({ threeLetterCode, userId });
  const { data: bankAccountData } = useGetPartnerAdminsUserBankAccount({ threeLetterCode, userId });

  const methods = useForm<EditUserFormBankAccountFieldValues>({
    defaultValues: convertToFormValues(data),
    criteriaMode: 'all',
    mode: 'onTouched',
    resolver: zodResolver(editUserFormBankAccountSchema),
  });

  const { trigger, isMutating } = usePutPartnerAdminsUserBankAccount(threeLetterCode, userId, {
    onSuccess: () => {
      showToast({
        message: '利用者情報を保存しました。',
        status: 'success',
      });
      navigate(getUserLink(userId));
    },
    onError: error => {
      const errors = error.response?.data?.errors;
      showToast({
        message: errors?.[0] ?? '利用者情報の更新に失敗しました。',
        status: 'danger',
      });
    },
  });

  const handleOnSubmit: PresentationProps['onSubmit'] = methods.handleSubmit(formValues => {
    trigger(generateRequest(formValues));
  });

  return {
    state: {
      methods,
      isButtonDisabled: methods.formState.isSubmitting || isMutating,
      bankAccountData,
      userData: data,
    },
    actions: { handleOnSubmit },
  };
};
